import { ScopeButton } from '@monorepo/shared-lib';
import { List } from 'antd';
import { ReactComponent as CheckIcon } from 'assets/images/icons/checkIcon.svg';
import { ReactComponent as IconClose } from 'assets/images/icons/iconClose.svg';
import ScanTransactionsImage from 'assets/images/scan-transactions.svg';
import { StyledCard } from 'components/Custom/card';
import {
  BlackSpan,
  CardDescBoldSpan,
  CardDescGreenSpan,
} from 'components/Custom/spans';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import routes from 'routes';
import { getAuthUser } from 'store/auth/selectors';
import {
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';
import styled from 'styled-components';

export const DOCUMENT_CAPTURE_BANNER_HIDE = 'document-capture-banner-hide';

const CAPTURE_FEATURE_KEYS = [
  'dashboard.document-capture-banner-feature-1',
  'dashboard.document-capture-banner-feature-2',
  'dashboard.document-capture-banner-feature-3',
  'dashboard.document-capture-banner-feature-4',
  'dashboard.document-capture-banner-feature-5',
] as const;

const DASHBOARD_GRID_COLS = 12;
const DASHBOARD_GRID_GAP = 16;
/** Split at grid centre (6+6): banner aligns with first three stat cards. */
const BANNER_CARD_COL_SPAN = 6;

const BannerRoot = styled.div`
  display: grid;
  grid-template-columns: repeat(${DASHBOARD_GRID_COLS}, minmax(0, 1fr));
  gap: ${DASHBOARD_GRID_GAP}px;
  width: 100%;
  margin-bottom: 24px;
  align-items: start;

  @media (max-width: 1199px) {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
`;

const BannerCard = styled(StyledCard)`
  grid-column: span ${BANNER_CARD_COL_SPAN};
  width: 100% !important;
  max-width: 100%;
  position: relative;
  min-width: 0;

  @media (max-width: 1199px) {
    grid-column: auto;
  }
`;

const BannerCardInner = styled.div`
  display: flex;
  justify-content: space-between;
  flex-flow: row;
  align-items: center;
  min-height: 200px;

  @media (max-width: 991px) {
    min-height: auto;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BannerContent = styled.div`
  padding: 28px 12px 28px 28px;
  flex: 1;
  min-width: 0;

  @media (max-width: 991px) {
    padding: 28px 20px 28px 28px;
  }

  @media (max-width: 767px) {
    padding: 24px 40px 16px 20px;
  }
`;

const BannerTitle = styled(CardDescBoldSpan)`
  display: block;
  margin-bottom: 20px;
  font-size: 28px;
  line-height: 1.15;

  @media (max-width: 991px) {
    font-size: 24px;
  }

  @media (max-width: 767px) {
    font-size: 20px;
    margin-bottom: 16px;
  }
`;

const BannerImage = styled.img`
  width: 150px;
  max-width: 30%;
  padding-right: 12px;
  flex-shrink: 0;

  @media (max-width: 991px) {
    width: 180px;
    max-width: 32%;
  }

  @media (max-width: 767px) {
    width: min(260px, 100%);
    max-width: 100%;
    padding: 0 20px 24px;
    align-self: center;
  }
`;

const BannerCta = styled(ScopeButton)`
  @media (max-width: 767px) {
    width: 100% !important;
    min-width: 0 !important;
  }
`;

const HeadlineBlock = styled.div`
  grid-column: span ${DASHBOARD_GRID_COLS - BANNER_CARD_COL_SPAN};
  min-width: 0;
  padding-top: 8px;
  padding-left: 40px;
  font-size: 28px;
  font-weight: 600;
  line-height: 32px;
  text-align: left;
  color: var(--color-text-dark, #042a2a);

  @media (max-width: 1199px) {
    grid-column: auto;
    width: 100%;
    padding-top: 0;
    padding-left: 0;
    font-size: 26px;
    line-height: 30px;
  }

  @media (max-width: 767px) {
    font-size: 22px;
    line-height: 26px;
  }
`;

const HeadlineAccent = styled(CardDescGreenSpan)`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.05;

  @media (max-width: 1199px) {
    font-size: 26px;
  }

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const IncludedPlanText = styled(CardDescGreenSpan)`
  display: block;
  margin-top: 40px;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.15;

  @media (max-width: 1199px) {
    font-size: 24px;
    margin-top: 32px;
  }

  @media (max-width: 767px) {
    font-size: 20px;
    margin-top: 28px;
  }
`;

export const DocumentCaptureBanner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector(getAuthUser);
  const prefs = usePreferences();

  const bannerHidden = Boolean(
    prefs(
      PreferenceActions.GET,
      PreferenceSections.APP,
      { name: DOCUMENT_CAPTURE_BANNER_HIDE },
      currentUser?.userUuid ?? ''
    )
  );

  const [optimisticallyHidden, setOptimisticallyHidden] = React.useState(false);

  React.useEffect(() => {
    if (!bannerHidden) {
      setOptimisticallyHidden(false);
    }
  }, [bannerHidden, currentUser?.prefDetails?.[1]]);

  const visible = !bannerHidden && !optimisticallyHidden;

  const handleHide = () => {
    setOptimisticallyHidden(true);
    if (currentUser?.userUuid) {
      prefs(
        PreferenceActions.SET,
        PreferenceSections.APP,
        { name: DOCUMENT_CAPTURE_BANNER_HIDE, value: true },
        currentUser.userUuid
      );
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <BannerRoot>
      <BannerCard
        $borderless
        $stopshadowbox
        $nopadding
        $removeHeader
        bg="linear-gradient(180deg, #EDFAF6 0%, #E1F4FE 100%)"
      >
        <IconClose
          role="button"
          tabIndex={0}
          aria-label={t('vat-checker.hide-contact')}
          onClick={handleHide}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleHide();
            }
          }}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            cursor: 'pointer',
            width: 16,
            height: 16,
            zIndex: 1,
            filter:
              'invert(71%) sepia(12%) saturate(247%) hue-rotate(131deg) brightness(80%) contrast(81%)',
          }}
        />
        <BannerCardInner>
          <BannerContent>
            <BannerTitle fontWeight="700">
              {t('dashboard.document-capture-banner-title')}
            </BannerTitle>
            <List
              style={{ marginBottom: 20 }}
              dataSource={CAPTURE_FEATURE_KEYS.map((key) => t(key))}
              split={false}
              size="small"
              renderItem={(item) => (
                <List.Item style={{ padding: '2px 3px' }}>
                  <CheckIcon
                    style={{
                      width: 15,
                      height: 15,
                      marginTop: 2,
                      flexShrink: 0,
                    }}
                  />{' '}
                  <BlackSpan>{item}</BlackSpan>
                </List.Item>
              )}
            />
            <BannerCta
              type="primary"
              width="auto"
              height="32px"
              fontSize="13px"
              lineheight="22px"
              onClick={() => navigate(routes.organisations.all)}
              style={{ padding: '0 16px', minWidth: 0, maxWidth: '100%' }}
            >
              {t('dashboard.document-capture-banner-cta')}
            </BannerCta>
          </BannerContent>
          <BannerImage alt="" src={ScanTransactionsImage} />
        </BannerCardInner>
      </BannerCard>
      <HeadlineBlock>
        <HeadlineAccent>{t('dashboard.document-capture-banner-headline-1')}</HeadlineAccent>
        <br />
        {t('dashboard.document-capture-banner-headline-2')} <br />
        {t('dashboard.document-capture-banner-headline-3')} <br />
        {t('billing.amplify-efficiency')}
        <IncludedPlanText>
          {t('dashboard.document-capture-banner-included-in-plan')}
        </IncludedPlanText>
      </HeadlineBlock>
    </BannerRoot>
  );
};
